import { useTranslation } from "react-i18next";

import type { Schedule } from "../../watchlist/types";
import { WEEKLY_DAYS } from "../../watchlist/types";

/** The three ways an alert's timing is written, shared by the list and the detail. */
export const useAlertLabels = () => {
  const { t } = useTranslation();

  return {
    /** Coarse "3h ago" — an alert's last run is never interesting to the minute. */
    relativeTime: (value: string): string => {
      const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));
      if (minutes < 60) return t("minutes_ago", { count: minutes });
      const hours = Math.round(minutes / 60);
      if (hours < 24) return t("hours_ago", { count: hours });
      return t("days_ago", { count: Math.round(hours / 24) });
    },

    /** Exact stamp for a run, which is read against the runs around it. */
    dateTime: (value: string): string =>
      new Date(value).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),

    /** `null` for the periodicities that have no cadence to write. */
    cadence: (schedule: Schedule | null): string | null =>
      schedule === null
        ? null
        : schedule.everyDays === WEEKLY_DAYS
          ? t("weekly")
          : t("watchlist.every_n_days", { count: schedule.everyDays }),
  };
};
